import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  FormLabel,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Address, AvailableDeliverySlots } from '../../../../client/services';
import { Users } from '../../../../store/users/api/interfaces';
import { useAppSelector } from '../../../../store';
import AddressCreateDialog from './AddressCreateDialog';

interface DeliveryData {
  addressId: string | null;
  deliveryDate: string | null;
  deliveryTimeSlot: string | null;
  isIdentitySecret: boolean;
  recipientName: string | null;
  recipientPhone: string | null;
}

interface AddressModel {
  id: string;
  addressType: string;
  addressName: string;
  houseNumber?: string;
  buildingNumber?: string;
  apartmentNumber?: string;
  floor?: string;
  officeNumber?: string;
  street: string;
  zone: string;
  extraDirection?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  isDefault?: boolean;
  regionId?: string;
  countryId?: string;
}

interface DeliverySelectorProps {
  selectedCustomer: Users | null;
  selectedCakes: any[];
  onDeliveryComplete: (delivery: DeliveryData) => void;
}

const DeliverySelector: React.FC<DeliverySelectorProps> = ({
  selectedCustomer,
  selectedCakes,
  onDeliveryComplete,
}) => {
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const [addresses, setAddresses] = useState<AddressModel[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [isIdentitySecret, setIsIdentitySecret] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [showRecipientDialog, setShowRecipientDialog] = useState(false);
  const [deliveryDays, setDeliveryDays] = useState<Date[]>([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [deliveryFee, _setDeliveryFee] = useState<number>(0); // Kept for future delivery fee calculation

  // Calculate delivery day gap from selected cakes (based on topping deliveryDayGap)
  useEffect(() => {
    let maxDeliveryDayGap = 0;
    selectedCakes.forEach((cake) => {
      // Assuming topping has deliveryDayGap property
      // You may need to fetch topping data to get this
      const gap = cake.topping?.deliveryDayGap || 0;
      if (gap > maxDeliveryDayGap) {
        maxDeliveryDayGap = gap;
      }
    });

    // Generate 7 days starting from deliveryDayGap
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + maxDeliveryDayGap);
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    setDeliveryDays(days);
    setSelectedDayIndex(0);
  }, [selectedCakes]);

  const getDeliveryGap = () =>
    selectedCakes.reduce((max, cake) => {
      const gap = Number(cake.topping?.deliveryDayGap) || 0;
      return gap > max ? gap : max;
    }, 0);

  const getOrderPoints = () => {
    const totalQty =
      selectedCakes?.reduce((sum, cake) => sum + (cake.qty || 1), 0) || 0;
    if (totalQty <= 0) {
      return 10;
    }

    return selectedCakes.reduce((sum, cake) => {
      const effortScore = cake.topping?.effortScore ?? 10;
      return sum + effortScore * (cake.qty || 1);
    }, 0);
  };

  const formatDateForApi = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadAvailableTimeSlots = async (selectedDate: Date) => {
    setIsLoadingTimeSlots(true);
    try {
      const response = await AvailableDeliverySlots.find({
        query: {
          date: formatDateForApi(selectedDate),
          points: getOrderPoints(),
          deliveryGap: getDeliveryGap(),
          acceptance: true,
        },
      });

      const requiredPoints = Number(response?.requiredPoints) || getOrderPoints();
      const availableSlots = (response?.slots || [])
        .filter((item: any) => {
          if (typeof item.isAvailable === 'boolean') {
            return item.isAvailable;
          }
          const remaining = Number(item.remainingPoints) || 0;
          return requiredPoints > 0
            ? remaining >= requiredPoints
            : remaining > 0;
        })
        .map((item: any) => item.slot);
      const validSlots = availableSlots.filter((slot: string) =>
        isTimeSlotValid(selectedDate, slot)
      );

      setTimeSlots(validSlots);

      if (validSlots.length === 0) {
        setSelectedTimeSlot('');
        return;
      }

      if (!validSlots.includes(selectedTimeSlot)) {
        setSelectedTimeSlot(validSlots[0]);
      }
    } catch (error) {
      console.error('Error loading available delivery slots:', error);
      setTimeSlots([]);
      setSelectedTimeSlot('');
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  useEffect(() => {
    if (deliveryDays.length > 0) {
      loadAvailableTimeSlots(deliveryDays[selectedDayIndex]);
    }
  }, [deliveryDays, selectedDayIndex, selectedCakes]);

  // Load customer addresses only if NOT a gift order
  useEffect(() => {
    if (selectedCustomer?.id && !isIdentitySecret) {
      loadAddresses();
    } else {
      setAddresses([]);
      setSelectedAddressId('');
    }
  }, [selectedCustomer?.id, isIdentitySecret, userRole]);

  const loadAddresses = async () => {
    if (!selectedCustomer?.id) return;

    setIsLoadingAddresses(true);
    try {
      // Build query parameters
      const queryParams: any = {
        limit: 100,
      };

      // If logged-in user is admin or orderManager, query addresses for selectedCustomer
      // Otherwise, backend will return addresses for logged-in user only
      if (userRole === 'admin' || userRole === 'orderManager') {
        queryParams.userId = selectedCustomer.id;
      }

      const response = await Address.find({
        query: queryParams,
      });

      // Response structure: { skip, limit, total, data: [...] }
      const addressesData = response?.data || (Array.isArray(response) ? response : []);
      
      if (Array.isArray(addressesData) && addressesData.length > 0) {
        setAddresses(addressesData);
        // Auto-select first/default address
        const defaultAddress = addressesData.find((addr) => addr.isDefault) || addressesData[0];
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          // Calculate delivery fee based on region (if available)
          // setDeliveryFee(defaultAddress.region?.deliveryFee || 0);
        }
      } else {
        // No addresses found for this customer
        setAddresses([]);
        setSelectedAddressId('');
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      setAddresses([]);
      setSelectedAddressId('');
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const isTimeSlotValid = (_selectedDate: Date, _timeSlot: string): boolean => {
    // Same-day windows are applied by available-delivery-slots (Qatar time).
    return true;
  };

  const handleDaySelect = (index: number) => {
    setSelectedDayIndex(index);
  };

  const handleSaveRecipient = () => {
    if (!recipientName.trim()) {
      setErrors({ ...errors, recipientName: 'Name is required' });
      return;
    }
    if (!recipientPhone.trim()) {
      setErrors({ ...errors, recipientPhone: 'Phone is required' });
      return;
    }
    if (recipientPhone === selectedCustomer?.mobileNumber) {
      setErrors({ ...errors, recipientPhone: 'Cannot use your own phone number' });
      return;
    }
    setShowRecipientDialog(false);
    setErrors({});
  };

  // Auto-validate and call onDeliveryComplete when all required fields are filled
  useEffect(() => {
    // For gift orders, address is not required - only recipient details and delivery date/time
    // For regular orders, address is required
    const hasAddress = isIdentitySecret ? true : selectedAddressId; // Gift orders don't need address

    if (hasAddress && selectedTimeSlot && deliveryDays.length > 0) {
      // If identity secret is enabled, recipient details must be provided
      if (isIdentitySecret && (!recipientName.trim() || !recipientPhone.trim())) {
        return; // Don't proceed yet
      }

      const selectedDay = deliveryDays[selectedDayIndex];
      const deliveryData: DeliveryData = {
        addressId: isIdentitySecret ? null : selectedAddressId, // No address needed for gift ordersin 
        // Use the Qatar-local calendar day (same basis as the slot-availability
        // query above). toISOString() would shift early-morning orders back a day.
        deliveryDate: formatDateForApi(selectedDay),
        deliveryTimeSlot: selectedTimeSlot,
        isIdentitySecret,
        recipientName: isIdentitySecret ? recipientName.trim() : null,
        recipientPhone: isIdentitySecret ? recipientPhone.trim() : null,
      };

      onDeliveryComplete(deliveryData);
    }
  }, [selectedAddressId, selectedTimeSlot, selectedDayIndex, isIdentitySecret, recipientName, recipientPhone, deliveryDays]);

  const selectedAddress = addresses.find((addr) => addr.id === selectedAddressId);

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant='h6' gutterBottom>
        Delivery Details
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Gift Order Toggle */}
        <Grid item xs={12}>
          <Card sx={{ p: 2, backgroundColor: '#FDF0F1' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isIdentitySecret}
                  onChange={(e) => setIsIdentitySecret(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#EE3750',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#EE3750',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    Keep My Identity Secret (Gift Order)
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Add shipping info for recipient instead of yourself
                  </Typography>
                </Box>
              }
            />
          </Card>
        </Grid>

        {/* Recipient Details (if gift order) */}
        {isIdentitySecret && (
          <Grid item xs={12}>
            <Card sx={{ p: 2, backgroundColor: '#FDF0F1', border: '1px solid #EE3750' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  {recipientName && recipientPhone ? (
                    <>
                      <Typography variant='body1' fontWeight='medium'>
                        {recipientName}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {recipientPhone}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Recipient Details
                    </Typography>
                  )}
                </Box>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => setShowRecipientDialog(true)}
                  sx={{
                    borderColor: '#EE3750',
                    color: '#EE3750',
                    '&:hover': {
                      borderColor: '#EE3750',
                      backgroundColor: '#FDF0F1',
                    },
                  }}
                >
                  {recipientName ? 'Edit' : 'Add'}
                </Button>
              </Box>
            </Card>
            {errors.recipient && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {errors.recipient}
              </FormHelperText>
            )}
          </Grid>
        )}

        {/* Shipping Address Selection - Only for non-gift orders */}
        {!isIdentitySecret && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <FormControl fullWidth error={!!errors.address}>
                <InputLabel>Select Delivery Address</InputLabel>
                {isLoadingAddresses ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Select
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value)}
                    label='Select Delivery Address'
                  >
                    {addresses.length === 0 ? (
                      <MenuItem disabled>
                        {selectedCustomer
                          ? 'No saved addresses found'
                          : 'Please select a customer first'}
                      </MenuItem>
                    ) : (
                      addresses.map((address) => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.addressName} - {address.street}, {address.zone}
                          {address.isDefault && ' (Default)'}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                )}
                {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
              </FormControl>
              {selectedCustomer && (userRole === 'admin' || userRole === 'orderManager') && (
                <Button
                  variant='outlined'
                  onClick={() => setShowAddressDialog(true)}
                  sx={{
                    borderColor: '#EE3750',
                    color: '#EE3750',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#EE3750',
                      backgroundColor: '#FDF0F1',
                    },
                  }}
                >
                  + Add New
                </Button>
              )}
            </Box>
            {selectedAddress && (
              <Box sx={{ mt: 1, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  <strong>Full Address:</strong> {selectedAddress.buildingNumber && `${selectedAddress.buildingNumber}, `}
                  {selectedAddress.street}, {selectedAddress.zone}
                  {selectedAddress.floor && `, Floor ${selectedAddress.floor}`}
                  {selectedAddress.apartmentNumber && `, Apt ${selectedAddress.apartmentNumber}`}
                </Typography>
              </Box>
            )}
          </Grid>
        )}

        {/* Address Create Dialog */}
        {selectedCustomer && (
          <AddressCreateDialog
            open={showAddressDialog}
            onClose={() => setShowAddressDialog(false)}
            onSuccess={(newAddress) => {
              // Reload addresses after successful creation
              loadAddresses();
              // Auto-select the newly created address
              if (newAddress?.id) {
                setSelectedAddressId(newAddress.id);
              }
            }}
            customerId={selectedCustomer.id}
            customerCountryCallingCode={selectedCustomer.countryCallingCode}
            customerMobileNumber={selectedCustomer.mobileNumber}
          />
        )}

        {/* Delivery Date Selection */}
        <Grid item xs={12}>
          <FormLabel component='legend' sx={{ mb: 1, display: 'block' }}>
            Select Delivery Day
          </FormLabel>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              pb: 1,
            }}
          >
            {deliveryDays.map((day, index) => {
              const isSelected = selectedDayIndex === index;
              const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNumber = day.getDate();

              return (
                <Box
                  key={index}
                  onClick={() => handleDaySelect(index)}
                  sx={{
                    minWidth: 80,
                    p: 2,
                    border: `1px solid ${isSelected ? 'transparent' : '#e0e0e0'}`,
                    borderRadius: 1,
                    backgroundColor: isSelected ? '#EE3750' : 'white',
                    color: isSelected ? 'white' : 'inherit',
                    cursor: 'pointer',
                    textAlign: 'center',
                    '&:hover': {
                      borderColor: '#EE3750',
                    },
                  }}
                >
                  <Typography variant='caption' fontWeight='bold' display='block'>
                    {dayName}
                  </Typography>
                  <Typography variant='h6' fontWeight='bold'>
                    {dayNumber}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>

        {/* Time Slot Selection */}
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.timeSlot}>
            <InputLabel>Select Time Slot</InputLabel>
            <Select
              value={selectedTimeSlot}
              onChange={(e) => setSelectedTimeSlot(e.target.value)}
              label='Select Time Slot'
              disabled={isLoadingTimeSlots}
            >
              {isLoadingTimeSlots && (
                <MenuItem value='' disabled>
                  Loading available slots...
                </MenuItem>
              )}
              {!isLoadingTimeSlots && timeSlots.length === 0 && (
                <MenuItem value='' disabled>
                  No slots available for this date
                </MenuItem>
              )}
              {timeSlots.map((slot) => {
                const isValid = deliveryDays.length > 0 && isTimeSlotValid(deliveryDays[selectedDayIndex], slot);
                return (
                  <MenuItem key={slot} value={slot} disabled={!isValid}>
                    {slot} {!isValid && '(Unavailable)'}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.timeSlot && <FormHelperText>{errors.timeSlot}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Delivery Fee Display */}
        {deliveryFee > 0 && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, backgroundColor: '#FDF0F1', borderRadius: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Delivery Fee:
              </Typography>
              <Typography variant='h6'>{deliveryFee.toFixed(2)} QAR</Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Recipient Details Dialog */}
      <Dialog open={showRecipientDialog} onClose={() => setShowRecipientDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Recipient Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Recipient Name *'
                value={recipientName}
                onChange={(e) => {
                  setRecipientName(e.target.value);
                  setErrors({ ...errors, recipientName: '' });
                }}
                error={!!errors.recipientName}
                helperText={errors.recipientName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Recipient Phone *'
                value={recipientPhone}
                onChange={(e) => {
                  setRecipientPhone(e.target.value);
                  setErrors({ ...errors, recipientPhone: '' });
                }}
                error={!!errors.recipientPhone}
                helperText={errors.recipientPhone || "Cannot be same as customer's phone"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRecipientDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRecipient}
            variant='contained'
            sx={{
              backgroundColor: '#EE3750 !important',
              '&:hover': {
                backgroundColor: '#EE3750 !important',
              },
            }}
          >
            {recipientName ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DeliverySelector;
